<template>
    <div class="ls-flow--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" test-id="form_wrapper">
            <div class="ls-flow--title">Confirm Change Provider</div>

            <form novalidate class="ls-flow--form" @submit.prevent="createPropertyChangeProvider">
                <LsSelectAllCheckboxes
                    v-model="selectAllCheckboxValues"
                    track-id="change_provider_view-select_all_checkboxes"
                    name="selectAllCheckboxes"
                    label="Choose service type(s) to change pro"
                    :validation="{ required: true }"
                    :options="selectAllCheckboxOptions"
                >
                    <template v-slot:option="{ option }">
                        <LsServiceDetails
                            :service="option"
                            :show-status-indicator="false"
                            :show-contractor-details="true"
                        />
                    </template>
                </LsSelectAllCheckboxes>

                <LsSelect
                    v-model="reason"
                    :options="reasonOptions"
                    track-id="change_provider_view-reason_options"
                    name="reason"
                    label="Reason for changing pro"
                    class="ls-flow--330"
                    :validation="{ required: true }"
                />

                <LsRadios
                    v-if="previousContractors.length"
                    v-model="sendToMarketplace"
                    track-id="change_provider_view-send_to_marketplace"
                    name="immediate"
                    label="Send to the marketplace"
                    :options="marketplaceOptions"
                    @input="changeSendToMarketplace"
                />

                <LsSelect
                    v-if="!sendToMarketplace"
                    v-model="selectedContractor"
                    name="selectedContractor"
                    :options="previousContractorOptions"
                    track-id="change_provider_view-previous_contractor_options"
                    class="ls-flow--330"
                    :validation="{ required: true }"
                />

                <LsSelect
                    v-if="!sendToMarketplace && selectedContractor"
                    v-model="selectedCrew"
                    :options="getCrewsForSelectedContractor(selectedContractor)"
                    name="selectedCrew"
                    track-id="change_provider_view-previous_crew_options"
                    class="ls-flow--330"
                    :validation="{ required: true }"
                />

                <LsInfoCard v-if="!sendToMarketplace">
                    Please ensure this provider is capable of doing the job(s)
                </LsInfoCard>

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="change_provider_view-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: true, min_value: 1000000, max: 8 }"
                />

                <LsCheckboxes
                    v-model="confirmChangeProValues"
                    track-id="change_provider_view-confirm_checkbox"
                    name="confirmProviderChange"
                    :validation="{ required: true }"
                    :options="confirmChangeProOptions"
                    style-override="font-weight:600;"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="change_provider_view-confirm_change_provider"
                    text="Confirm Change Provider"
                    :loading="isUpdating"
                    type="submit"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService, lsFilters, lsModalService } from 'ls-vue-common';
import gql from 'graphql-tag';

import { lsHelpers } from '@/helpers';
import Schedule from '@/graphql/queries/Schedule.gql';
import CreatePropertyChangeProvider from '@/graphql/mutations/CreatePropertyChangeProvider.gql';
import {
    LsButton,
    LsRadios,
    LsInput,
    LsCheckboxes,
    LsSelect,
    LsInfoCard,
} from '@/components/common';
import LsSelectAllCheckboxes from './components/LsSelectAllCheckboxes/LsSelectAllCheckboxes.vue';
import { LsServiceDetails } from '@/components';
import { FlowSuccessModal } from '@/modals';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import { apolloClient } from '@/vue-apollo';
import LsError from '@/components/common/LsError/LsError.vue';
import AllActiveOrPendingServices from '@/graphql/queries/AllActiveOrPendingServices.gql';
import PropertyChangeProvider from '@/graphql/queries/PropertyChangeProvider.gql';

const reasonOptions = [
    {
        label: 'Incomplete job',
        value: 'customer.job.incomplete',
    },
    {
        label: 'Property damage',
        value: 'customer.property.damage',
    },
    {
        label: 'Quality issue',
        value: 'customer.quality.issue',
    },
    {
        label: 'Scheduling issue',
        value: 'customer.scheduling.issue',
    },
    {
        label: 'Customer did not specify',
        value: 'customer.no_reason_provided',
    },
    {
        label: 'Provider initiated',
        value: 'provider.initiated',
    },
    {
        label: 'Provider fraud',
        value: 'ops.provider.fraud',
    },
    {
        label: 'Other',
        value: 'ops.other',
    },
];

export default {
    name: 'ChangeProviderView',
    components: {
        LsButton,
        LsRadios,
        LsInput,
        LsTemplatedLoading,
        LsError,
        LsSelectAllCheckboxes,
        LsServiceDetails,
        LsSelect,
        LsInfoCard,
        LsCheckboxes,
    },
    $_veeValidate: {
        validator: 'new',
    },
    data() {
        return {
            loading: 0,
            isUpdating: false,
            isInvalid: false,
            reasonOptions,

            selectAllCheckboxOptions: [],
            previousContractors: [],
            property: apolloClient.readFragment({
                id: `Property:${this.$route.params.property_id}`,
                fragment: gql`
                    fragment property on Property {
                        street
                    }
                `,
            }),

            // form data
            selectAllCheckboxValues: [],
            sendToMarketplace: true,
            marketplaceOptions: [
                { label: 'Yes', value: true },
                { label: 'No, assign a previous provider', value: false },
            ],
            confirmChangeProOptions: [
                {
                    label: `I have confirmed that the provider needs to be changed for the selected
                        service types.`,
                    value: false,
                },
            ],
            confirmChangeProValues: [],
            selectedContractor: null,
            reason: null,
            selectedCrew: null,
            zendesk_ticket_id: '',
        };
    },
    apollo: {
        allActiveOrPendingServices: {
            query: AllActiveOrPendingServices,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.selectAllCheckboxOptions = data.allActiveOrPendingServices;
                }
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.allActiveOrPendingServices.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
        },
        propertyChangeProvider: {
            query: PropertyChangeProvider,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.previousContractors = data.propertyChangeProvider.contractors;
                    this.previousContractorOptions = _.map(
                        this.previousContractors,
                        (previousContractor) => {
                            const { user, name, id } = previousContractor;
                            return {
                                label: `${name} - ${lsFilters.fullName({ user })}`,
                                value: id,
                            };
                        }
                    );
                }
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.propertyChangeProvider.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
        },
    },
    methods: {
        async createPropertyChangeProvider() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }
            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreatePropertyChangeProvider,
                    variables: _.pickBy(
                        {
                            customer_id: _.toNumber(this.$route.params.customer_id),
                            property_id: _.toNumber(this.$route.params.property_id),
                            schedules: this.getSelectedSchedules(),
                            reason: this.reason,
                            contractor_id: _.toNumber(this.selectedContractor) || null,
                            crew_id: this.selectedCrew || null,
                            zendesk_ticket_id: this.zendesk_ticket_id
                                ? _.toInteger(this.zendesk_ticket_id)
                                : null,
                        },
                        _.identity
                    ),
                    refetchQueries: [
                        {
                            query: Schedule,
                            variables: {
                                customer_id: _.toNumber(this.$route.params.customer_id),
                                property_id: _.toNumber(this.$route.params.property_id),
                                schedule_id: _.toNumber(this.$route.params.schedule_id),
                            },
                        },
                    ],
                })
                .then(({ data }) => {
                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title: `Provider Changed for ${this.property.street}`,
                            copy: data.createPropertyChangeProvider.copy,
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
        getCrewsForSelectedContractor(selectedContractorId) {
            const selectedContractor = _.find(this.previousContractors, {
                id: selectedContractorId,
            });

            return _.map(selectedContractor.crews, (crew) => {
                return {
                    label: crew.name,
                    value: crew.id,
                };
            });
        },
        getSelectedSchedules() {
            return _.map(this.selectAllCheckboxValues, (checkboxValue) => {
                return {
                    schedule_id: checkboxValue,
                };
            });
        },
        changeSendToMarketplace() {
            if (this.sendToMarketplace) {
                this.selectedContractor = null;
                this.selectedCrew = null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.change-provider-view-confirm-change-checkbox {
    padding-bottom: 20px;
    font-weight: bold;
}
</style>
